import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Modal, Radio, RadioGroup, Textarea } from '@opendate-app/setlist';
import { formatCents, giftCardShape } from './OrderGiftCardsCard';

export const VOID_ONLY = 'void';
export const VOID_AND_REFUND = 'refund';

const usageSummary = (giftCard, money) => {
  if (giftCard.unused) {
    return `This card hasn't been used. Refunding returns the full ${money(giftCard.refundableCents)} the buyer paid for it.`;
  }
  return `${money(giftCard.spentCents)} of this card has been spent. Only the remaining ${money(giftCard.refundableCents)} can be refunded.`;
};

const GiftCardVoidRefundModal = ({ giftCard, submitting, onClose, onSubmit }) => {
  const refundAvailable = Boolean(giftCard && giftCard.canRefundPurchase && !giftCard.refundRefusal);
  const voidAvailable = Boolean(giftCard && giftCard.canVoid && !giftCard.voidRefusal);
  const [choice, setChoice] = useState(refundAvailable ? VOID_AND_REFUND : VOID_ONLY);
  const [reason, setReason] = useState('');

  if (!giftCard) return null;

  const money = (cents) => formatCents(cents, giftCard.currency);
  const alreadyVoided = giftCard.status === 'voided';
  const refunding = choice === VOID_AND_REFUND;
  const missingReason = !reason.trim();
  const choiceAvailable = refunding ? refundAvailable : voidAvailable;

  const footer = (
    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
      <Button
        variant="destructive"
        onClick={() => onSubmit(choice, reason)}
        disabled={submitting || missingReason || !choiceAvailable}
      >
        {refunding ? `${alreadyVoided ? 'Refund' : 'Void and refund'} ${money(giftCard.refundableCents)}` : 'Void card'}
      </Button>
    </div>
  );

  return (
    <Modal open onClose={onClose} icon={false} title={`Void or refund gift card •••• ${giftCard.last4}`} footer={footer}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {refundAvailable && <p style={{ color: 'var(--sl-text-secondary)', margin: 0 }}>{usageSummary(giftCard, money)}</p>}
        {giftCard.refundRefusal && <Alert color="gray" size="full-width" title={giftCard.refundRefusal} />}
        <RadioGroup name={`gift-card-${giftCard.id}-action`} value={choice} onChange={setChoice}>
          <Radio value={VOID_ONLY} label="Void only" disabled={!voidAvailable} />
          <Radio
            value={VOID_AND_REFUND}
            label={`${alreadyVoided ? 'Refund the buyer' : 'Void and refund the buyer'}${refundAvailable ? ` ${money(giftCard.refundableCents)}` : ''}`}
            disabled={!refundAvailable}
          />
        </RadioGroup>
        <Textarea
          label="Reason"
          rows={3}
          placeholder="Why is this card being voided?"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Alert
          color="warning"
          size="full-width"
          title="This can't be undone"
          description={refunding
            ? `The card stops working and the refund goes back to the card the buyer paid with.${giftCard.bonusValueCents > 0 ? ` Its ${money(giftCard.bonusValueCents)} bonus card is voided too, including any unspent bonus moved onto another card.` : ''}`
            : 'The card stops working right away. The buyer is not refunded.'}
        />
      </div>
    </Modal>
  );
};

GiftCardVoidRefundModal.propTypes = {
  giftCard: giftCardShape,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

GiftCardVoidRefundModal.defaultProps = {
  giftCard: null,
  submitting: false,
};

export default GiftCardVoidRefundModal;
